import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { soilManagementService } from '../../services/soilManagementService';
import {
  Plus,
  Search,
  Edit,
  Trash2
} from 'lucide-react';

const SoilManagement = () => {
  const [soilTypes, setSoilTypes] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSoil, setEditingSoil] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isFetching, setIsFetching] = useState(false); // table loading (doesn't flicker on mutations)
  const [isMutating, setIsMutating] = useState(false); // add/edit/delete operation state
  const [apiStatus, setApiStatus] = useState('checking'); // 'checking', 'connected', 'offline'
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [userRole, setUserRole] = useState('user'); // 'user', 'supervisor', 'admin'

  const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

  // Mock data for fallback based on simplified structure
  const mockSoilTypes = [
    {
      id: 1,
      name: "Red Soil",
      created_at: "2025-08-26T03:59:01.576Z",
      updated_at: "2025-08-26T03:59:01.576Z",
      code: "SM001",
      status: "active"
    },
    {
      id: 2,
      name: "Black Soil",
      created_at: "2025-08-26T03:59:01.576Z",
      updated_at: "2025-08-26T03:59:01.576Z",
      code: "SM002",
      status: "active"
    },
    {
      id: 3,
      name: "Alluvial Soil",
      created_at: "2025-08-26T03:59:01.576Z",
      updated_at: "2025-08-26T03:59:01.576Z",
      code: "SM003",
      status: "active"
    },
    {
      id: 4,
      name: "Laterite Soil",
      created_at: "2025-08-26T03:59:01.576Z",
      updated_at: "2025-08-26T03:59:01.576Z",
      code: "SM004",
      status: "active"
    }
  ];

  // Load soil types from API with fallback to mock data
  const loadSoilTypes = async (options = {}) => {
    const silent = options.silent === true; // when true, don't toggle table spinner
    try {
      setApiStatus('checking');
      if (!silent) setIsFetching(true);

      // Try API first
      const response = await soilManagementService.getSoilTypes({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined
      });

      if (response.success && response.data) {
        // Handle the API response structure
        const soilData = response.data;
        if (Array.isArray(soilData)) {
          // If data is an array, it's the old format
          setSoilTypes(soilData);
          setPagination(prev => ({
            ...prev,
            total: soilData.length,
            pages: 1
          }));
        } else if (Array.isArray(soilData.soilTypes)) {
          // New format with pagination
          setSoilTypes(soilData.soilTypes);
          setPagination(prev => ({
            ...prev,
            total: soilData.pagination?.total || soilData.total || 0,
            pages: soilData.pagination?.pages || Math.ceil((soilData.pagination?.total || soilData.total || 0) / pagination.limit)
          }));
        } else if (Array.isArray(soilData)) {
          // Direct array format
          setSoilTypes(soilData);
          setPagination(prev => ({
            ...prev,
            total: response.pagination?.total || soilData.length || 0,
            pages: response.pagination?.pages || Math.ceil((response.pagination?.total || soilData.length || 0) / pagination.limit)
          }));
        } else {
          throw new Error('Invalid data structure');
        }
        setApiStatus('connected');
      } else {
        throw new Error(response.message || 'Invalid API response');
      }
    } catch (error) {
      console.warn('API failed, using mock data:', error.message);
      setApiStatus('offline');

      // Fallback to mock data with filtering (only essential fields)
      let filteredData = mockSoilTypes;

      if (searchTerm) {
        filteredData = mockSoilTypes.filter(soil => 
          soil.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (soil.code && soil.code.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      if (filterStatus !== 'all') {
        filteredData = filteredData.filter(soil => soil.status === filterStatus);
      }

      setSoilTypes(filteredData);
      setPagination(prev => ({
        ...prev,
        total: filteredData.length,
        pages: 1
      }));
    } finally {
      if (!silent) setIsFetching(false);
    }
  };

  // Check API connectivity
  const checkApiStatus = async () => {
    try {
      setApiStatus('checking');
      await soilManagementService.getSoilTypes({ limit: 1 });
      setApiStatus('connected');
      setUserRole('user'); // Set default role for logged-in users
      return true;
    } catch (error) {
      setApiStatus('offline');
      setUserRole('user');
      return false;
    }
  };

  // Auto-generate code
  const generateCode = () => {
    if (!soilTypes || soilTypes.length === 0) return 'SO001';
    const maxCode = Math.max(...soilTypes.map(soil => parseInt(soil?.code?.replace('SO', '') || '0')), 0);
    const nextNumber = maxCode + 1;
    return `SO${nextNumber.toString().padStart(3, '0')}`;
  };

  // Load data on component mount
  useEffect(() => {
    loadSoilTypes();
    checkApiStatus();
  }, []);

  // Reload data when search or filter changes
  useEffect(() => {
    if (apiStatus === 'connected') {
      // Reset to first page when search or filter changes
      setPagination(prev => ({ ...prev, page: 1 }));
      loadSoilTypes();
    } else {
      // For offline mode, filter locally (only essential fields)
      let filteredData = mockSoilTypes;

      if (searchTerm) {
        filteredData = mockSoilTypes.filter(soil => 
          soil.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (soil.code && soil.code.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      if (filterStatus !== 'all') {
        filteredData = filteredData.filter(soil => soil.status === filterStatus);
      }

      setSoilTypes(filteredData);
      setPagination(prev => ({
        ...prev,
        total: filteredData.length,
        pages: 1
      }));
    }
  }, [searchTerm, filterStatus]);

  // Reload data when pagination changes
  useEffect(() => {
    if (apiStatus === 'connected' && pagination.page > 1) {
      loadSoilTypes();
    }
  }, [pagination.page]);

  const handleAdd = async (data) => {
    setIsMutating(true);
    try {
      // Optimistic add to UI
      const optimistic = {
        id: Date.now(),
        code: generateCode(),
        name: data.name,
        status: data.status || 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setSoilTypes(prev => [optimistic, ...prev]);
      setPagination(prev => ({ ...prev, total: prev.total + 1 }));

      // Try API first
      if (apiStatus === 'connected') {
        const response = await soilManagementService.createSoilType({
          code: optimistic.code, // Auto-generate code
          name: data.name,
        status: data.status || 'active'
        });

        if (response.success) {
      toast.success('Soil type added successfully!');
          // Replace optimistic item with server item if provided
          const created = response.data || response.soil || response.soilType || null;
          if (created && created.id) {
            setSoilTypes(prev => prev.map(s => (s.id === optimistic.id ? created : s)));
          } else {
            // Refresh silently to sync
            loadSoilTypes({ silent: true });
          }
        } else {
          throw new Error(response.message || 'Failed to add soil type');
        }
      } else {
        toast.success('Soil type added successfully! (Local mode)');
      }

      setShowAddModal(false);
      reset();
    } catch (error) {
      console.error('Add soil type error:', error);
      // Revert optimistic add
      setSoilTypes(prev => prev.filter(s => s.id !== undefined && typeof s.id === 'number' ? s.id !== undefined && s.id !== prev[0]?.id : true));
      setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));

      if (error.message.includes('Admin/Supervisor privileges required')) {
        toast.error('Access denied. Admin/Supervisor privileges required to add soil types.');
      } else {
        toast.error(error.message || 'Failed to add soil type');
      }
    } finally {
      setIsMutating(false);
    }
  };

  const handleEdit = async (data) => {
    if (!editingSoil) return;
    setIsMutating(true);
    try {
      // Optimistic update
      const updatedSoil = { 
        ...editingSoil, 
        code: editingSoil.code,
        name: data.name,
        status: data.status,
        updated_at: new Date().toISOString()
      };
      setSoilTypes(prev => prev.map(soil => soil.id === editingSoil.id ? updatedSoil : soil));

      // API
      if (apiStatus === 'connected') {
        const response = await soilManagementService.updateSoilType(editingSoil.id, {
          code: editingSoil.code,
          name: data.name,
          status: data.status
        });

        if (response.success) {
      toast.success('Soil type updated successfully!');
          // Optionally sync from server silently
          loadSoilTypes({ silent: true });
        } else {
          throw new Error(response.message || 'Failed to update soil type');
        }
      } else {
        toast.success('Soil type updated successfully! (Local mode)');
      }

      setShowAddModal(false);
      setEditingSoil(null);
      reset();
    } catch (error) {
      console.error('Update soil type error:', error);
      toast.error(error.message || 'Failed to update soil type');
      // On error, silently refresh to restore server truth when online
      if (apiStatus === 'connected') loadSoilTypes({ silent: true });
    } finally {
      setIsMutating(false);
    }
  };

  const handleDelete = async (soilId) => {
    if (!window.confirm('Are you sure you want to delete this soil type?')) return;

    setIsMutating(true);
    // Optimistic remove
    const previous = soilTypes;
    setSoilTypes(prev => prev.filter(soil => soil.id !== soilId));
    setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));

    try {
      // API
      if (apiStatus === 'connected') {
        const response = await soilManagementService.deleteSoilType(soilId);
        if (response.success) {
      toast.success('Soil type deleted successfully!');
          // Keep current state; optionally silent refresh
          loadSoilTypes({ silent: true });
          return;
        }
        throw new Error(response.message || 'Failed to delete soil type');
      } else {
        toast.success('Soil type deleted successfully! (Local mode)');
      }
    } catch (error) {
      console.error('Delete soil type error:', error);
      toast.error(error.message || 'Failed to delete soil type');
      // Revert optimistic remove
      setSoilTypes(previous);
      setPagination(prev => ({ ...prev, total: previous.length }));
    } finally {
      setIsMutating(false);
    }
  };

  const openEditModal = (soil) => {
    if (!soil) return;
    setEditingSoil(soil);
    setValue('code', soil?.code || '');
    setValue('name', soil?.name || '');
    setValue('status', soil?.status || 'active');
    setShowAddModal(true);
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  // Allow all logged-in users full access
  const canCreate = true;
  const canEdit = true;
  const canDelete = true;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Soil Type Management</h1>
          <p className="text-gray-600">Manage soil types for agricultural trials</p>
        </div>
        
        <button 
          onClick={() => {
            setEditingSoil(null);
            reset();
            // Pre-fill with auto-generated code
            setValue('code', generateCode());
            setShowAddModal(true);
          }}
          className="btn-primary flex items-center mt-4 sm:mt-0"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Soil Type
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search soil types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          
          {/* Refresh Button */}
          <button
            onClick={() => loadSoilTypes({ silent: false })}
            disabled={isFetching}
            className="p-2 text-gray-400 hover:text-gray-600 disabled:text-gray-300 transition-colors"
            title="Refresh data"
          >
            
          </button>
        </div>
      </div>

      {/* Soil Types Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Soil Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isFetching ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mr-3"></div>
                      <span className="text-gray-500">Loading soil types...</span>
                    </div>
                  </td>
                </tr>
              ) : soilTypes.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <p className="text-lg font-medium">No soil types found</p>
                      <p className="text-sm">Get started by adding your first soil type</p>
                    </div>
                  </td>
                </tr>
              ) : (
                soilTypes.map((soil) => (
                  <tr key={soil?.id || 'unknown'} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono font-semibold text-blue-600">{soil?.code || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{soil?.name || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(soil?.status)}`}>
                        {soil?.status || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEditModal(soil)}
                        className="text-blue-600 hover:text-blue-800"
                          title="Edit Soil Type"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                          onClick={() => handleDelete(soil?.id)}
                        className="text-red-600 hover:text-red-800"
                          title="Delete Soil Type"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Info */}
        {pagination.total > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-700">
              <span>
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </span>
              <span className="text-xs text-gray-500">
                {apiStatus === 'offline' && '(Local Mode)'}
              </span>
            </div>
            
            {/* Pagination Controls */}
            {apiStatus === 'connected' && pagination.total > pagination.limit && (
              <div className="flex items-center justify-center space-x-2 mt-3">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-700">
                  Page {pagination.page} of {pagination.pages || Math.ceil(pagination.total / pagination.limit)}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= (pagination.pages || Math.ceil(pagination.total / pagination.limit))}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingSoil ? 'Edit' : 'Add'} Soil Type
              </h2>
              
              {/* API Status Note */}
              {apiStatus === 'offline' && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> Currently in local mode. Changes will be saved locally only.
                  </p>
                </div>
              )}
              
              <form onSubmit={handleSubmit(editingSoil ? handleEdit : handleAdd)} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <input
                      type="text"
                    {...register('code', { required: 'Code is required' })}
                    className="input-field bg-gray-100 cursor-not-allowed"
                    placeholder="e.g., SO001, SO002"
                    maxLength={10}
                      readOnly
                    />
                  <p className="text-xs text-gray-500 mt-1">
                    {editingSoil ? 'Code cannot be edited' : 'Code will be generated automatically'}
                  </p>
                  {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>}
                  </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Soil Type Name</label>
                  <input
                    type="text"
                    {...register('name', { required: 'Soil type name is required' })}
                    className="input-field"
                    placeholder="e.g., Red Soil, Black Soil"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    defaultValue="active"
                    {...register('status', { required: 'Status is required' })}
                    className="input-field"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingSoil(null);
                      reset();
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isMutating}
                    className="btn-primary"
                  >
                    {isMutating ? 'Saving...' : (editingSoil ? 'Update' : 'Add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoilManagement; 